import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import axios from 'axios'
import { destinationApresConnexion, useAuth, type DefiMfa, type User } from './AuthContext'
import { Mail } from 'lucide-react'
import Logo from './Logo'
import LienAccueil from './components/LienAccueil'
import { BoutonRenvoyerLien } from './components/AvisAdresseNonVerifiee'
import { BOUTON_PRINCIPAL, CARTE_COMPTE, CHAMP, FOND_COMPTE, LIBELLE, LIEN, LIEN_DISCRET } from './charte/compte'

import MfaConnexion, { type SessionMfa } from './MfaConnexion'
import { retourSur } from './retourConnexion'

const API = (import.meta.env.VITE_API_URL || '') + '/api'

interface Attente { id: number; commune_insee: string; nom_commune: string; commune_role: string }

export default function LoginPage() {
  const [defi, setDefi] = useState<DefiMfa | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // Fix S4-7 (morceau 2) : une invitation en attente pour MON email s'affiche à la
  // connexion — REJOINDRE en un clic, plus jamais le cul-de-sac « il fallait rouvrir le lien ».
  const [attente, setAttente] = useState<Attente | null>(null)
  // Lot 0 : « Rejoindre » sans le lien de l'invitation exige une adresse confirmée. Non confirmée,
  // l'écran dit pourquoi et propose de renvoyer le lien ; le serveur, lui, revérifie (403).
  const [adresseVerifiee, setAdresseVerifiee] = useState(true)
  const [joining, setJoining] = useState(false)
  const { login, setSession, user, isAdmin, sessionExpiree, oublierSessionExpiree } = useAuth()
  const navigate = useNavigate()
  // SE-3 (CDC GBM du 27/10) : « Se reconnecter » et les routes réservées passent la page à
  // retrouver ; la connexion y ramène, après le filet des invitations. L'avis d'expiration est
  // lu une fois à l'arrivée, puis oublié : quitter la connexion ne le fait pas réapparaître.
  const [params] = useSearchParams()
  const retour = retourSur(params.get('retour'))
  const [sessionEchue] = useState(() => !!sessionExpiree)
  useEffect(() => { if (sessionExpiree) oublierSessionExpiree() }, [sessionExpiree, oublierSessionExpiree])

  // F31 (relevé 17/07, re-confirmé punch-list 24/07) : déjà connecté, /login réaffichait
  // le formulaire. Même destination que le post-login (admin → /admin, client → SA commune).
  // Lot 0 (revue codex P2) : déjà connecté, on regarde AVANT de rediriger s'il attend une
  // invitation — c'est le retour de la page de confirmation d'adresse (« Continuer » mène ici) :
  // sans ce filet, l'invitation qu'il vient de débloquer n'était plus proposée. Une fois par
  // visite (« Plus tard » ou une erreur ne la reproposent pas) ; un échec ne bloque jamais.
  const filet = useRef<'a-faire' | 'en-cours' | 'fait'>('a-faire')
  const montee = useRef(true)
  useEffect(() => { montee.current = true; return () => { montee.current = false } }, [])
  useEffect(() => {
    if (!user || attente) return
    if (isAdmin) { navigate(retour ?? '/admin', { replace: true }); return }
    if (user.role === 'analyst' || filet.current === 'fait') {
      navigate(retour ?? destinationApresConnexion(user), { replace: true }); return
    }
    if (filet.current === 'en-cours') return      // la session relue (/auth/me) ne relance rien
    filet.current = 'en-cours'
    const suite = (liste: Attente[], verifiee?: boolean) => {
      filet.current = 'fait'
      if (!montee.current) return
      if (liste.length > 0) {
        setAdresseVerifiee(verifiee !== false)
        setAttente(liste[0])
      } else navigate(retour ?? destinationApresConnexion(user), { replace: true })
    }
    axios.get<{ invitations: Attente[]; adresse_verifiee?: boolean }>(`${API}/mes-invitations`)
      .then(r => suite(r?.data?.invitations ?? [], r?.data?.adresse_verifiee))
      .catch(() => suite([]))
  }, [user, isAdmin, navigate, attente, retour])

  const apresConnexion = async (u: User) => {
    // Après login : admin → dashboard ; client → directement SA commune (abonnement ou
    // inscription — demande Marc 13/07) ; sans commune de référence → la recherche (/#recherche).
    // Retour d'une session expirée (SE-3) : la page qu'on avait sous les yeux remplace la
    // destination finale, APRÈS le filet des invitations (revue Codex P2).
    if (u.role === 'admin' || u.role === 'direction') { navigate(retour ?? '/admin'); return }
    // Un consultant (ABS, 14/10) n'a que ses études : pas d'invitation d'espace à lui proposer.
    if (u.role === 'analyst') { navigate(retour ?? '/etudes'); return }
    // Le filet invitation passe AVANT la redirection (codex Majeur 27/07) : un compte
    // « lead » porte une commune_reference auto-déclarée SANS être membre — le
    // rediriger d'abord lui cacherait sa vraie invitation. Le check est borné à
    // l'email de la session et ne bloque jamais la connexion.
    try {
      const r = await axios.get<{ invitations: Attente[]; adresse_verifiee?: boolean }>(`${API}/mes-invitations`)
      if (r.data.invitations.length > 0) {
        setAdresseVerifiee(r.data.adresse_verifiee !== false)
        setAttente(r.data.invitations[0])
        return
      }
    } catch { /* le filet ne doit jamais bloquer la connexion */ }
    // Compte d'intercommunalité (CDC H) → la vue de SON EPCI ; sinon SA commune ou la recherche.
    navigate(retour ?? destinationApresConnexion(u))
  }

  const ouvrirSessionMfa = (session: SessionMfa) => {
    const u = { email, role: session.role, name: session.name,
      commune_reference: session.commune_reference, carte_nationale: session.carte_nationale, espace: session.espace }
    setDefi(null)
    setSession(session.token, u)
    void apresConnexion(u)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const u = await login(email, password)
      setPassword('')
      if ('defi' in u) { setDefi(u); return }
      await apresConnexion(u)
    } catch (err) {
      // #715 : une démonstration terminée répond 403 avec sa raison ; tout le reste garde le
      // message unique (pas d'indice sur l'existence d'un compte).
      const r = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response
      setError(r?.status === 403 && typeof r.data?.detail === 'string'
        ? r.data.detail : 'Email ou mot de passe incorrect')
    } finally {
      setLoading(false)
    }
  }

  const rejoindre = async () => {
    if (!attente) return
    setJoining(true)
    try {
      await axios.post(`${API}/mes-invitations/${attente.id}/rejoindre`)
      navigate(`/o/${attente.commune_insee}/synthese`)
    } catch (err) {
      const r = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response
      if (r?.status === 403) { setAdresseVerifiee(false); return }   // lot 0 : adresse à confirmer
      setError("Impossible de rejoindre l'espace pour le moment — réessayez ou rouvrez le lien d'invitation.")
      setAttente(null)
    } finally {
      setJoining(false)
    }
  }

  if (defi) return <MfaConnexion key={defi.defi} defi={defi} onSession={ouvrirSessionMfa}
    onRecommencer={() => { setDefi(null); setPassword(''); setError('') }} />

  if (attente) {
    return (
      <div className={FOND_COMPTE}>
        <div className={`w-full max-w-md ${CARTE_COMPTE} text-center space-y-4`}>
          <Mail size={28} className="mx-auto text-(--c-brand)" />
          <h2 className="text-lg font-semibold text-(--c-brand)">Une invitation vous attend</h2>
          <p className="text-sm text-(--c-text)">
            Vous êtes invité·e à rejoindre l'espace observatoire de{' '}
            <strong>{attente.nom_commune}</strong>.
          </p>
          {adresseVerifiee ? (
            <button onClick={rejoindre} disabled={joining}
              className={BOUTON_PRINCIPAL}>
              {joining ? 'Ouverture…' : "Rejoindre l'espace"}
            </button>
          ) : (
            <>
              <p className="text-sm text-(--c-text)">
                Pour rejoindre cet espace, confirmez d'abord votre adresse électronique avec le lien
                que nous vous avons envoyé, ou ouvrez le lien reçu dans le courriel d'invitation.
              </p>
              <BoutonRenvoyerLien className={BOUTON_PRINCIPAL} />
            </>
          )}
          <button onClick={() => { setAttente(null); navigate('/#recherche') }}
            className={LIEN_DISCRET}>
            Plus tard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={FOND_COMPTE}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <LienAccueil className="inline-flex items-center">
            <Logo size="lg" />
          </LienAccueil>
          {/* « Espace Administration » sur la page où un CLIENT se connecte : relevé au
              dossier §G, revu à la recette visuelle du 07/09. Les pages sœurs nomment
              l'action de l'utilisateur — « Créer un compte », « Mot de passe oublié » —
              et celle-ci nommait notre console interne. Un maire doit lire qu'il entre
              chez lui, pas dans notre back-office. */}
          <p className="mt-3 text-(--c-muted)">Connexion à votre espace</p>
        </div>

        <form onSubmit={handleSubmit} className={`${CARTE_COMPTE} space-y-5`}>
          {sessionEchue && (
            <p role="status" className="text-sm text-(--c-text) bg-(--c-bg) border border-(--c-border) rounded-(--radius-sm) px-4 py-2">
              Votre session a expiré. Reconnectez-vous{retour ? ' pour revenir sur votre page' : ''}.
            </p>
          )}
          <div>
            <label htmlFor="login-email" className={LIBELLE}>Email</label>
            {/* Le placeholder était `admin@l-obster.fr` : il CONFIRMAIT l'adresse réelle
                du compte administrateur à tout visiteur — la moitié d'un identifiant
                offerte. On reprend le motif de « Mot de passe oublié » plutôt que
                d'inventer une troisième forme : il dit à qui la page s'adresse. */}
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={CHAMP}
              placeholder="vous@collectivite.fr"
              required
            />
          </div>
          <div>
            <label htmlFor="login-password" className={LIBELLE}>Mot de passe</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className={CHAMP}
              placeholder="Mot de passe"
              required
            />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-(--radius-sm) px-4 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={BOUTON_PRINCIPAL}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                Connexion...
              </span>
            ) : 'Se connecter'}
          </button>

          <div className="text-center pt-2">
            <Link to="/forgot-password" className={LIEN}>
              Mot de passe oublié ?
            </Link>
          </div>
        </form>

        <div className="text-center mt-6 space-y-2">
          <Link to="/register" className={`block ${LIEN}`}>
            Pas encore inscrit ? Créer un compte
          </Link>
          <Link to="/" className={`${LIEN_DISCRET} inline-flex items-center gap-1`}>
            Retour au site
          </Link>
        </div>
      </div>
    </div>
  )
}
